import { useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { identityClient } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'react-toastify';
import { getApiErrorMessage } from '@/utils/apiError';
import { Loader2, CheckCircle2, XCircle, Eye, EyeOff } from 'lucide-react';

interface InvitationInfo {
  email: string;
  tenantId: number;
  expiresAt: string;
}

interface AcceptInvitationRequest {
  token: string;
  password: string;
  fullName?: string;
  phoneNumber?: string;
}

export function AcceptInvitePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Validate token
  const { data: invitation, isLoading: validating, error: validationError } = useQuery({
    queryKey: ['invitation', token],
    queryFn: async () => {
      const response = await identityClient.get<InvitationInfo>(`/admin/users/invitation/validate?token=${token}`);
      return response.data;
    },
    enabled: !!token,
    retry: false,
  });

  // Accept invitation mutation
  const acceptMutation = useMutation({
    mutationFn: async (data: AcceptInvitationRequest) => {
      // Backend expects PascalCase property names
      const response = await identityClient.post('/admin/users/invitation/accept', {
        Token: data.token,
        Password: data.password,
        FullName: data.fullName,
        PhoneNumber: data.phoneNumber,
      });
      return response.data;
    },
    onSuccess: () => {
      toast.success(t('auth.acceptInvite.accountCreated', 'Account created successfully!'));
      setTimeout(() => {
        navigate('/admin-login');
      }, 2000);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, t('auth.acceptInvite.acceptInvitationFailed')));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (password !== confirmPassword) {
      toast.error(t('auth.acceptInvite.passwordMismatch', 'Passwords do not match'));
      return;
    }

    if (password.length < 6) {
      toast.error(t('auth.acceptInvite.passwordTooShort', 'Password must be at least 6 characters'));
      return;
    }

    acceptMutation.mutate({
      token,
      password,
      fullName: fullName || undefined,
    });
  };

  // No token provided
  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('auth.acceptInvite.invalidLink', 'Invalid Link')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('auth.acceptInvite.noTokenProvided', 'No invitation token was provided.')}
          </p>
          <Button onClick={() => navigate('/admin-login')}>
            {t('auth.acceptInvite.goToLogin', 'Go to Login')}
          </Button>
        </div>
      </div>
    );
  }

  // Validating token
  if (validating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
          <p className="text-gray-600 dark:text-gray-400">
            {t('auth.acceptInvite.validatingInvitation', 'Validating invitation...')}
          </p>
        </div>
      </div>
    );
  }

  // Invalid or expired token
  if (validationError) {
    const errorMessage = getApiErrorMessage(validationError, t('auth.acceptInvite.invalidInvitation'));
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('auth.acceptInvite.invitationError', 'Invitation Error')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {errorMessage}
          </p>
          <Button onClick={() => navigate('/admin-login')}>
            {t('auth.acceptInvite.goToLogin', 'Go to Login')}
          </Button>
        </div>
      </div>
    );
  }

  // Success state after accepting
  if (acceptMutation.isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
        <div className="max-w-md w-full bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8 text-center">
          <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('auth.acceptInvite.welcomeAboard', 'Welcome Aboard!')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {t('auth.acceptInvite.accountCreatedRedirecting', 'Your account has been created. Redirecting to login...')}
          </p>
          <Loader2 className="w-6 h-6 animate-spin text-primary mx-auto" />
        </div>
      </div>
    );
  }

  // Show form
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4" dir="rtl">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-block bg-gradient-to-br from-primary to-primary/80 px-6 py-3 rounded-xl mb-4">
            <span className="text-3xl font-bold text-white">مكسب</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('auth.acceptInvite.title', 'Accept Invitation')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">
            {t('auth.acceptInvite.subtitle', 'Create your account to get started')}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl p-8">
          <div className="mb-6 p-4 bg-primary/5 rounded-lg border border-primary/20">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.acceptInvite.invitedAs', 'You have been invited as')}
            </p>
            <p className="font-semibold text-gray-900 dark:text-white mt-1">
              {invitation?.email}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="fullName">{t('auth.acceptInvite.fullName', 'Full Name')}</Label>
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder={t('auth.acceptInvite.enterFullName', 'Enter your full name')}
                className="mt-1"
              />
            </div>

            <div>
              <Label htmlFor="password">{t('auth.acceptInvite.password', 'Password')} *</Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={t('auth.acceptInvite.enterPassword', 'Enter password')}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div>
              <Label htmlFor="confirmPassword">{t('auth.acceptInvite.confirmPassword', 'Confirm Password')} *</Label>
              <div className="relative mt-1">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder={t('auth.acceptInvite.confirmPasswordPlaceholder', 'Confirm your password')}
                  required
                  minLength={6}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && confirmPassword && password !== confirmPassword && (
                <p className="text-sm text-red-500 mt-1">
                  {t('auth.acceptInvite.passwordMismatch', 'Passwords do not match')}
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="w-full"
              disabled={acceptMutation.isPending || !password || password !== confirmPassword}
            >
              {acceptMutation.isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin ml-2" />
                  {t('auth.acceptInvite.creatingAccount', 'Creating account...')}
                </>
              ) : (
                t('auth.acceptInvite.createAccount', 'Create Account')
              )}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {t('auth.acceptInvite.alreadyHaveAccount', 'Already have an account?')}{' '}
              <button
                onClick={() => navigate('/admin-login')}
                className="text-primary hover:underline font-medium"
              >
                {t('auth.acceptInvite.login', 'Login')}
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AcceptInvitePage;
